import React, { useState } from 'react';
import { PropersAssignedVisits } from '../models/dashboardModels';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardTitle,
  Table,
} from 'reactstrap';
import {
  IconCalendar,
  IconChevronRight,
  IconClock,
  IconHeart,
  IconMail,
  IconMapPin,
  IconPhone,
} from '@tabler/icons-react';
import VisitMarketplaceTypeBadge from '../../../components/VisitMarketplaceTypeBadge';
import {
  getBadgeColorVisitStatusAdapter,
  getVisitStatusAdapter,
} from '../../properties/adapters/visitsAdapter';
import FeedbackProgressPill from './FeedbackProgressPill';
import { VISIT_STATUS } from '../../../constants/visitsConstants';
import FavoritesViewButton from './FavoritesViewButton';
import IntentFactBanner from './IntentFactBanner';
import IntentSignalsCell from './IntentSignalsCell';
import InvestorFavoritesModal from './InvestorFavoritesModal';

export interface Props {
  visits: PropersAssignedVisits[];
  footer?: React.ReactNode;
  showIntentBanner?: boolean;
}

const DashboardVisitManagerListTable = ({
  visits,
  footer,
  showIntentBanner = false,
}: Props) => {
  const [favoritesModalVisitId, setFavoritesModalVisitId] = useState<
    string | null
  >(null);

  const favoritesModalVisit = visits.find(
    (visit) => visit.id === favoritesModalVisitId,
  );

  const navigate = useNavigate();
  return (
    <Card className="dv2-table">
        <CardHeader>
          <CardTitle>Visitas</CardTitle>
        </CardHeader>
        <CardBody>
          {showIntentBanner && <IntentFactBanner />}
          <div className="table-responsive">
            <Table striped hover>
              <thead>
                <tr>
                  <th>Propiedad</th>
                  <th>Inquilino</th>
                  <th>Proper/SDR</th>
                  <th style={{ width: 260, maxWidth: 260 }}>
                    Señales de intención
                  </th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {visits.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted">
                      No hay visitas registradas.
                    </td>
                  </tr>
                )}
                {visits.map((visit) => (
                  <tr key={visit.id}>
                    <td>
                      <div>
                        <div className="font-weight-bold">
                          [{visit.propertyInfo.code}] {visit.propertyInfo.name}
                        </div>
                        <div className="d-flex align-items-center text-muted small">
                          <IconMapPin size={12} className="me-1" />
                          {visit.propertyInfo.address}
                        </div>
                        <div className="text-success font-weight-bold">
                          {visit.propertyInfo.price}
                        </div>
                        <div>
                          <VisitMarketplaceTypeBadge
                            marketplace_type={visit.visitInfo.marketplace_type}
                          />
                          {visit.propertyInfo.is_favorite && (
                            <Badge color="danger" className="ml-2">
                              <IconHeart size={12} className="me-1" />
                              En sus favoritos
                            </Badge>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div className="font-weight-bold">
                          {visit.visitInfo.tenant_name}
                        </div>
                        <div className="d-flex align-items-center text-muted">
                          <IconMail size={12} className="me-1" />{' '}
                          {visit.visitInfo.tenant_email}
                        </div>
                        <div className="d-flex align-items-center text-muted">
                          <IconPhone size={12} className="me-1" />
                          {visit.visitInfo.tenant_phone}
                        </div>
                        <div className="d-flex align-items-center font-weight-bold">
                          <IconCalendar size={12} className="me-1" />
                          {visit.visitInfo.visit_date}
                        </div>
                        <div className="d-flex align-items-center text-muted">
                          <IconClock size={12} className="me-1" />
                          {visit.visitInfo.visit_time}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div className="font-weight-bold">
                          (Proper) {visit.properInfo.name}
                        </div>
                        {visit.sdrInfo?.name && (
                          <div className="font-weight-bold">
                            (SDR) {visit.sdrInfo.name}
                          </div>
                        )}
                      </div>
                    </td>
                    <td
                      style={{
                        width: 260,
                        maxWidth: 260,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                      }}
                    >
                      <IntentSignalsCell
                        favorites={visit.visitInfo.tenant_favorites}
                        visitDate={visit.visitInfo.visit_date}
                        visitTime={visit.visitInfo.visit_time}
                        status={visit.visitInfo.status}
                        onViewFavorites={() =>
                          setFavoritesModalVisitId(visit.id)
                        }
                      />
                    </td>
                    <td>
                      <Badge
                        className="badge-lg mr-2"
                        color={getBadgeColorVisitStatusAdapter(
                          visit.visitInfo.status,
                        )}
                      >
                        {getVisitStatusAdapter(visit.visitInfo.status)}
                      </Badge>
                      {visit.visitInfo.status === VISIT_STATUS.DONE && (
                        <div>
                          <FeedbackProgressPill
                            visitId={visit.id}
                            service="ts"
                            hasProperFeedback={visit.visitInfo.has_feedback}
                          />
                        </div>
                      )}
                      <FavoritesViewButton
                        favorites={visit.visitInfo.tenant_favorites}
                        onClick={() => setFavoritesModalVisitId(visit.id)}
                      />
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <button
                          type="button"
                          className="dv2-detail-link"
                          onClick={() =>
                            navigate(`/dashboard/rent-visits/${visit.id}`, {
                              state: { visit },
                            })
                          }
                        >
                          Ver detalle
                          <IconChevronRight size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </CardBody>
        <CardFooter>{footer && <div>{footer}</div>}</CardFooter>
      {favoritesModalVisit && (
        <InvestorFavoritesModal
          isOpen={!!favoritesModalVisitId}
          toggle={() => setFavoritesModalVisitId(null)}
          investorName={favoritesModalVisit.visitInfo.tenant_name}
          favorites={favoritesModalVisit.visitInfo.tenant_favorites || []}
          currentListingId={favoritesModalVisit.propertyInfo.id}
        />
      )}
    </Card>
  );
};

export default DashboardVisitManagerListTable;
